import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { DisplayOrderService } from '../../../services/display_order/display-order.service';

// "Set Display Order" popup — shared by the menu category, page category and
// carousel list pages. Replaces opening each record to type a display index by
// hand: the whole published list is shown at once and dragged into order, then
// saved in a single bulk call.
//
// Only the ORDER is sent; the server renumbers 1..n. That keeps the client from
// having to reason about gaps, duplicates or which device/language rows exist —
// the reorder applies to every device/language variant of each record.
@Component({
  selector: 'app-display-order',
  templateUrl: './display-order.component.html',
  styleUrls: ['./display-order.component.scss']
})
export class DisplayOrderComponent implements OnInit {

    // Entity base path from environment, e.g. environment.getMenuCategoryUrl.
    @Input() baseUrl: string;
    // Shown in the popup header, e.g. "Menu Category".
    @Input() entityLabel: string = 'Items';

    // Emits true when the order was saved (so the list can refresh), false on cancel.
    @Output() displayOrderClosed = new EventEmitter<boolean>();

    items: any[] = [];
    menuCategoryOptions: any[] = [];
    // Ids in their original order — lets us detect "nothing actually moved".
    private originalIds: string = '';

    loading: boolean = true;
    saving: boolean = false;
    loadError: string = '';
    searchTerm: string = '';
    selectedMenuCategoryId: string = '';

    constructor(
        private displayOrderService: DisplayOrderService,
        private toastr: ToastrService
    ) { }

    ngOnInit() {
        this.load();
    }

    load() {
        this.loading = true;
        this.loadError = '';
        this.displayOrderService.getDisplayOrder(this.baseUrl).subscribe(
            (result: any) => {
                const data = (result && result.data) ? result.data : {};
                this.items = (data.items || []).slice();
                this.menuCategoryOptions = this.buildMenuCategoryOptions(this.items);
                this.originalIds = this.currentIds().join(',');
                this.loading = false;
            },
            () => {
                this.loadError = 'Could not load the current order. Please close and try again.';
                this.loading = false;
            }
        );
    }

    private currentIds(): any[] {
        return this.items.map((item) => item.id);
    }

    private buildMenuCategoryOptions(items: any[]): any[] {
        const categoryMap = {};
        const categoryOptions = [];

        (items || []).forEach((item) => {
            (item.menuCategories || []).forEach((category) => {
                if (category && category.id !== undefined && category.id !== null && !categoryMap[category.id]) {
                    categoryMap[category.id] = true;
                    categoryOptions.push({
                        id: String(category.id),
                        title: category.title || ('#' + category.id)
                    });
                }
            });
        });

        return categoryOptions.sort((first, second) => {
            return String(first.title).localeCompare(String(second.title));
        });
    }

    get isDirty(): boolean {
        return this.currentIds().join(',') !== this.originalIds;
    }

    get visibleItems(): any[] {
        const term = (this.searchTerm || '').trim().toLowerCase();
        const selectedMenuCategoryId = this.selectedMenuCategoryId;

        return this.items.filter((item) => {
            if (term && !this.matchesSearch(item, term)) {
                return false;
            }
            if (selectedMenuCategoryId && !this.hasMenuCategory(item, selectedMenuCategoryId)) {
                return false;
            }
            return true;
        });
    }

    private matchesSearch(item: any, term: string): boolean {
        if (String(item.title || '').toLowerCase().indexOf(term) !== -1) {
            return true;
        }
        return (item.menuCategories || []).some((category) => {
            return String((category && category.title) || '').toLowerCase().indexOf(term) !== -1;
        });
    }

    private hasMenuCategory(item: any, selectedMenuCategoryId: string): boolean {
        return (item.menuCategories || []).some((category) => {
            return category && String(category.id) === selectedMenuCategoryId;
        });
    }

    hasMenuCategories(item: any): boolean {
        return item && item.menuCategories && item.menuCategories.length > 0;
    }

    drop(event: CdkDragDrop<any[]>) {
        if (event.previousIndex === event.currentIndex) {
            return;
        }
        this.reorderVisibleItems(event.previousIndex, event.currentIndex);
    }

    // Keyboard/click alternative to dragging — also the accessible path.
    moveUp(index: number) {
        if (index <= 0) {
            return;
        }
        this.reorderVisibleItems(index, index - 1);
    }

    moveDown(index: number) {
        if (index >= this.visibleItems.length - 1) {
            return;
        }
        this.reorderVisibleItems(index, index + 1);
    }

    moveToTop(index: number) {
        if (index <= 0) {
            return;
        }
        this.reorderVisibleItems(index, 0);
    }

    private reorderVisibleItems(previousIndex: number, currentIndex: number) {
        const visibleItems = this.visibleItems.slice();
        if (
            previousIndex < 0 ||
            currentIndex < 0 ||
            previousIndex >= visibleItems.length ||
            currentIndex >= visibleItems.length
        ) {
            return;
        }

        const visibleIdMap = {};
        visibleItems.forEach((item) => {
            visibleIdMap[item.id] = true;
        });

        moveItemInArray(visibleItems, previousIndex, currentIndex);

        let visibleIndex = 0;
        this.items = this.items.map((item) => {
            if (visibleIdMap[item.id]) {
                return visibleItems[visibleIndex++];
            }
            return item;
        });
    }

    reset() {
        this.searchTerm = '';
        this.selectedMenuCategoryId = '';
        this.load();
    }

    save() {
        if (this.saving || !this.items.length) {
            return;
        }
        if (!this.isDirty) {
            this.toastr.info('The order has not changed.');
            return;
        }

        this.saving = true;
        this.displayOrderService.saveDisplayOrder(this.baseUrl, this.currentIds()).subscribe(
            (result: any) => {
                this.saving = false;
                this.toastr.success((result && result.message) || 'Display order updated');
                this.displayOrderClosed.emit(true);
            },
            (error: any) => {
                this.saving = false;
                const message = error && error.error && error.error.message
                    ? error.error.message
                    : 'Could not save the display order.';
                this.toastr.error(message);
            }
        );
    }

    close() {
        this.displayOrderClosed.emit(false);
    }
}
